// Pure calc helpers for the Billing tab.
//
// Input shape (matches backend `TreasuryRevenueLine` schema, camelCase or
// snake_case both tolerated for forward-compat):
//   { label, monthly_values: number[12], payment_lag: number, vat_applicable: bool }
//
// monthly_values is CALENDAR-FIXED : index 0 = January, index 11 = December.
//
// Scenario "year 1" = the 12 consecutive months starting at start_month.
// For a scenario starting in May (start_month = 4) :
//   - May invoice (calendar idx 4) is offset 0 in the scenario.
//   - April invoice (calendar idx 3) is offset 11 (last month of year 1).
//
// VAT base: "facturé" (at invoice month). Cash collection uses payment_lag
// in months : a facture at scenario-offset o lands at offset o+lag ; if
// o+lag >= 12 it spills into year 2 and is dropped from cashYear1.

const MONTHS = 12;

const num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

function getMonthlyValues(line) {
  const arr = line.monthly_values || line.monthlyValues || [];
  const out = new Array(MONTHS).fill(0);
  for (let i = 0; i < MONTHS; i += 1) out[i] = num(arr[i]);
  return out;
}

function getPaymentLag(line) {
  return Math.max(0, Math.floor(num(line.payment_lag ?? line.paymentLag)));
}

function getVatApplicable(line) {
  return line.vat_applicable ?? line.vatApplicable ?? false;
}

export function lineTotal(line) {
  return getMonthlyValues(line).reduce((s, v) => s + v, 0);
}

const clampStart = (m) => (Number.isInteger(m) && m >= 0 && m <= 11 ? m : 0);

// Returns array indexed by scenario offset (0 = first month of the scenario)
// summing HT amounts across all lines.
export function monthlyHt(revenues, startMonth = 0) {
  const s = clampStart(startMonth);
  const out = new Array(MONTHS).fill(0);
  (revenues || []).forEach((line) => {
    const values = getMonthlyValues(line);
    for (let m = 0; m < MONTHS; m += 1) {
      const offset = (m - s + MONTHS) % MONTHS;
      out[offset] += values[m];
    }
  });
  return out;
}

export function monthlyVat(revenues, vatRate, startMonth = 0) {
  const rate = num(vatRate) / 100;
  const s = clampStart(startMonth);
  const out = new Array(MONTHS).fill(0);
  (revenues || []).forEach((line) => {
    if (!getVatApplicable(line)) return;
    const values = getMonthlyValues(line);
    for (let m = 0; m < MONTHS; m += 1) {
      const offset = (m - s + MONTHS) % MONTHS;
      out[offset] += values[m] * rate;
    }
  });
  return out;
}

// Cash collection TTC[12] indexed by scenario offset.
// Facture at calendar month m → scenario offset (m - startMonth) mod 12.
// Cash lands at offset + payment_lag ; if that's >= 12 it spills to year 2
// and is dropped here.
export function monthlyCashCollection(revenues, vatRate, startMonth = 0) {
  const rate = num(vatRate) / 100;
  const s = clampStart(startMonth);
  const out = new Array(MONTHS).fill(0);
  (revenues || []).forEach((line) => {
    const values = getMonthlyValues(line);
    const lag = getPaymentLag(line);
    const vatMul = getVatApplicable(line) ? 1 + rate : 1;
    for (let m = 0; m < MONTHS; m += 1) {
      const offset = (m - s + MONTHS) % MONTHS;
      const dest = offset + lag;
      if (dest < MONTHS) out[dest] += values[m] * vatMul;
    }
  });
  return out;
}

export function totalAnnualHt(revenues) {
  return monthlyHt(revenues).reduce((s, v) => s + v, 0);
}

export function totalAnnualVat(revenues, vatRate) {
  // Sum doesn't depend on the month order.
  return monthlyVat(revenues, vatRate, 0).reduce((s, v) => s + v, 0);
}

export function totalAnnualTtc(revenues, vatRate) {
  return totalAnnualHt(revenues) + totalAnnualVat(revenues, vatRate);
}

export function cashYear1(revenues, vatRate, startMonth = 0) {
  return monthlyCashCollection(revenues, vatRate, startMonth).reduce((s, v) => s + v, 0);
}

export function weightedAvgPaymentLag(revenues) {
  let weightedSum = 0;
  let totalWeight = 0;
  (revenues || []).forEach((line) => {
    const total = lineTotal(line);
    if (total <= 0) return;
    weightedSum += getPaymentLag(line) * total;
    totalWeight += total;
  });
  return totalWeight > 0 ? weightedSum / totalWeight : 0;
}

export function activeLineCount(revenues) {
  return (revenues || []).filter((line) => lineTotal(line) > 0).length;
}

export function computeBillingKpis(revenues, vatRate, startMonth = 0) {
  const caHt = totalAnnualHt(revenues);
  const caTtc = totalAnnualTtc(revenues, vatRate);
  return {
    caHt,
    caTtc,
    cashYear1: cashYear1(revenues, vatRate, startMonth),
    avgPaymentLag: weightedAvgPaymentLag(revenues),
    activeLines: activeLineCount(revenues),
  };
}
